import { Component, OnInit, OnChanges, SimpleChanges, inject, input, output, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ErsGridComponent } from '@rcs/grid';
import { MonthYearPickerComponent, MonthYearChange } from '../../month-year-picker/month-year-picker.component';
import { EmployeeCellRendererComponent } from '../../employee-cell-renderer/employee-cell-renderer.component';
import { ManagerLeavePanelComponent } from '../../manager-leave-panel/manager-leave-panel.component';
import { EmployeeLeaveBalanceComponent } from '../employee-leave-balance/employee-leave-balance.component';
import { LeaveRequestRow, HourlyPermissionRow } from '../../../models/leave.model';
import { statusBadgeRenderer, managerActionRenderer, hourlyManagerActionRenderer } from '../../../utils/grid-cell-renderers';
import { formatDateMDY, parseLocalDate } from '../../../utils/date.utils';
import { SuccessDialogComponent } from '../../../dialogs/success-dialog/success-dialog';
import { ManagerDeclineDialogComponent, ManagerDeclineDialogResult } from '../../../dialogs/manager-decline-dialog/manager-decline-dialog.component';
import { DeleteDialogComponent } from '../../../dialogs/delete-dialog/delete-dialog.component';
import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-enterprise';
import { dateFilterParamsDMY } from '@rcs/utils';
import { LeaveService } from '../../../services/leave.service';
import { LmLoadingOverlayComponent } from '../../lm-loading-overlay/lm-loading-overlay.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, Observable } from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'ers-employee-leave-request',
  standalone: true,
  imports: [
    TranslocoPipe,CommonModule, ErsGridComponent, MonthYearPickerComponent, EmployeeCellRendererComponent, ManagerLeavePanelComponent, EmployeeLeaveBalanceComponent, LmLoadingOverlayComponent],
  templateUrl: './employee-leave-request.component.html',
  styleUrls: ['./employee-leave-request.component.scss'],
})
export class EmployeeLeaveRequestComponent implements OnInit, OnChanges {
  private leaveService = inject(LeaveService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  isManager = input(false);
  canViewAllRequests = input(false);
  canViewLeaveBalanceTab = input(false);
  employeeSection = input<'requests' | 'balance'>('requests');
  employeeSectionChange = output<'requests' | 'balance'>();

  /** When user is both HR and Manager, this controls the active perspective */
  viewMode: 'hr' | 'manager' = 'manager';

  /** True when the toggle should be visible (user is both HR and Manager) */
  get showViewToggle(): boolean {
    return this.isManager() && this.canViewAllRequests();
  }

  /** Effective manager flag — true in manager mode or when user is manager-only */
  get effectiveIsManager(): boolean {
    if (this.showViewToggle) return this.viewMode === 'manager';
    return this.isManager();
  }

  columnDefs: (ColDef | ColGroupDef)[] = [];
  rowData: LeaveRequestRow[] = [];
  managerPanelRowData: LeaveRequestRow[] = [];
  managerHourlyPanelRowData: LeaveRequestRow[] = [];

  hourlyColumnDefs: (ColDef | ColGroupDef)[] = [];
  hourlyRowData: HourlyPermissionRow[] = [];

  pendingLeaveCount = 0;
  pendingHourlyCount = 0;
  activeTab: 'leave' | 'hourly' | 'scheduledLeaves' | 'scheduledHourly' | 'leaveBalance' = 'leave';
  private lastRequestsTab: 'leave' | 'hourly' | 'scheduledLeaves' | 'scheduledHourly' = 'leave';

  customGridOptions: Partial<GridOptions> = {};

  isLoading = true;
  isHourlyLoading = true;
  isManagerPanelLoading = false;
  isProcessing = false;
  currentMonth = new Date().getMonth();
  currentYear = new Date().getFullYear();
  totalRequests = 0;
  totalHourlyRequests = 0;

  private viewInitialized = false;

  get canAccessRequests(): boolean {
    return this.isManager() || this.canViewAllRequests();
  }

  get showPendingBadges(): boolean {
    return this.effectiveIsManager;
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'hr' ? 'manager' : 'hr';
    // If switching away from manager mode while on a manager-only tab, go back to leave
    if (this.viewMode === 'hr' && (this.activeTab === 'scheduledLeaves' || this.activeTab === 'scheduledHourly')) {
      this.activeTab = 'leave';
      this.lastRequestsTab = 'leave';
    }
    this.setupColumnDefs();
    this.reloadAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employeeSection']) {
      this.syncActiveTabFromSection();
    }

    if (!changes['isManager'] && !changes['canViewAllRequests']) return;

    this.setupColumnDefs();

    if (!this.viewInitialized) return;

    this.reloadAll();
  }

  ngOnInit(): void {
    this.syncActiveTabFromSection();
    this.setupColumnDefs();
    this.setupGridOptions();
    this.reloadAll();
    this.viewInitialized = true;
  }



  onPickerChange(event: MonthYearChange): void {
    this.currentMonth = event.month;
    this.currentYear = event.year;
    this.reloadAll();
  }

  setActiveTab(tab: 'leave' | 'hourly' | 'scheduledLeaves' | 'scheduledHourly'): void {
    this.lastRequestsTab = tab;
    if (this.employeeSection() === 'balance') {
      this.employeeSectionChange.emit('requests');
    }
    this.activeTab = tab;
  }

  openLeaveBalanceTab(): void {
    this.employeeSectionChange.emit('balance');
    this.activeTab = 'leaveBalance';
  }

  private syncActiveTabFromSection(): void {
    if (this.employeeSection() === 'balance') {
      this.activeTab = 'leaveBalance';
      return;
    }

    if (this.activeTab === 'leaveBalance') {
      this.activeTab = this.lastRequestsTab;
    }
  }

  onAllGridCellClick(event: any): void {
    if (!this.effectiveIsManager) return;

    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;

    const target = (mouseEvent.target as HTMLElement)?.closest('[data-action]') as HTMLElement | null;
    if (!target) return;
    if (target instanceof HTMLButtonElement && target.disabled) return;

    const action = target.getAttribute('data-action');
    const id = event.data?.id ?? target.getAttribute('data-id');
    const name = event.data?.employeeName ?? '';

    if (action === 'cancel') {
      this.cancelRequest(id, name, false);
    } else {
      this.handleManagerAction(event, false);
    }
  }

  onHourlyGridCellClick(event: any): void {
    if (!this.effectiveIsManager) return;

    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;

    const target = (mouseEvent.target as HTMLElement)?.closest('[data-action]') as HTMLElement | null;
    if (!target) return;
    if (target instanceof HTMLButtonElement && target.disabled) return;

    const action = target.getAttribute('data-action');
    const id = event.data?.id ?? target.getAttribute('data-id');
    const name = event.data?.employeeName ?? '';

    if (action === 'approve') {
      this.approveRequest(id, name, true);
    } else if (action === 'decline') {
      this.declineRequest(id, name, true);
    } else if (action === 'cancel') {
      this.cancelRequest(id, name, true);
    }
  }

  private handleManagerAction(event: any, isHourly: boolean): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;

    const target = (mouseEvent.target as HTMLElement)?.closest('[data-action]') as HTMLElement | null;
    if (!target) return;

    const action = target.getAttribute('data-action');
    const id = event.data?.id ?? target.getAttribute('data-id');
    const name = event.data?.employeeName ?? '';

    if (action === 'approve') {
      this.approveRequest(id, name, isHourly);
    } else if (action === 'decline') {
      this.declineRequest(id, name, isHourly);
    }
  }

  private approveRequest(id: string, employeeName: string, isHourly: boolean): void {
    this.isProcessing = true;
    this.leaveService.processLeaveRequest({ leaveId: id, action: 'Approve' })
      .pipe(catchError(() => of({ success: false, message: '' })))
      .subscribe(res => {
        this.isProcessing = false;
        if ((res as any).success === false) return;

        const title = isHourly
          ? `Hourly Permission has been Approved for ${employeeName}`
          : `Leave Request has Approved for ${employeeName}`;

        this.dialog.open(SuccessDialogComponent, {
          width: '600px',
          panelClass: 'lm-dialog-panel',
          data: {
            title,
            desc: 'The approval notification has been sent to the employee.',
          },
        });

        this.reloadAll();
      });
  }

  private declineRequest(id: string, employeeName: string, isHourly: boolean): void {
    const ref = this.dialog.open(ManagerDeclineDialogComponent, {
      width: '460px',
      panelClass: 'lm-dialog-panel',
      data: { employeeName, isHourly },
    });

    ref.afterClosed().subscribe((result: ManagerDeclineDialogResult | undefined) => {
      if (!result?.confirmed) return;

      this.isProcessing = true;
      this.leaveService.processLeaveRequest({
        leaveId: id,
        action: 'Reject',
        rejectionReason: result.reason,
      })
      .pipe(catchError(() => of({ success: false })))
      .subscribe(() => {
        this.isProcessing = false;
        this.reloadAll();
      });
    });
  }

  private cancelRequest(id: string, employeeName: string, isHourly: boolean): void {
    if (!id) return;
    const ref = this.dialog.open(DeleteDialogComponent, {
      width: '480px',
      panelClass: 'lm-dialog-panel',
      data: {
        title: isHourly ? 'Cancel Hourly Permission' : 'Cancel Leave Request',
        itemName: employeeName,
        message: isHourly
          ? 'This action will cancel the hourly permission request and reverse approved effects. Are you sure?'
          : 'This action will cancel the leave request and reverse approved effects. Are you sure?',
        confirmText: 'Yes, Cancel Request',
        cancelText: 'Keep Request',
      },
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.isProcessing = true;
      this.leaveService.cancelLeaveRequest(id)
        .pipe(catchError(() => of({ success: false, error: 'Unable to cancel request' })))
        .subscribe(() => {
          this.isProcessing = false;
          this.reloadAll();
        });
    });
  }

  private getTodayStart(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private isTodayOrFuture(value: string | Date | null | undefined): boolean {
    const d = this.safeDate(value);
    if (!d) return false;
    d.setHours(0, 0, 0, 0);
    return d >= this.getTodayStart();
  }

  private toManagerViewStatus(status: string | null | undefined): string {
    return (status ?? '').trim() || '—';
  }

  private reloadAll(): void {
    if (!this.canAccessRequests) {
      this.rowData = [];
      this.hourlyRowData = [];
      this.totalRequests = 0;
      this.totalHourlyRequests = 0;
      this.pendingLeaveCount = 0;
      this.pendingHourlyCount = 0;
      this.isLoading = false;
      this.isHourlyLoading = false;
      return;
    }

    this.loadLeaveAndHourlyData();

    if (!this.effectiveIsManager) {
      this.pendingLeaveCount = 0;
      this.pendingHourlyCount = 0;
      this.managerPanelRowData = [];
      this.managerHourlyPanelRowData = [];
      this.isManagerPanelLoading = false;
    }
  }

  private setupColumnDefs(): void {
    const leaveColumns: (ColDef | ColGroupDef)[] = [
      { headerName: 'Date', field: 'date', sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY, width: 110 },
      { headerName: 'Employee', field: 'employeeName', sortable: true, filter: 'agSetColumnFilter', flex: 1, minWidth: 180, cellRenderer: EmployeeCellRendererComponent, wrapText: true, autoHeight: true },
      { headerName: 'Emp ID', field: 'empId', sortable: true, filter: 'agSetColumnFilter', width: 100 },
      { headerName: 'Department', field: 'department', sortable: true, filter: 'agSetColumnFilter', width: 120 },
      { headerName: 'Leave Type', field: 'leaveType', sortable: true, filter: 'agSetColumnFilter', width: 130 },
      {
        headerName: 'Days Requested',
        marryChildren: true,
        children: [
          { headerName: 'From', field: 'from', sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY, width: 110 },
          { headerName: 'To', field: 'to', sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY, width: 110 },
          { headerName: 'Total Days', field: 'totalDays', sortable: true, filter: false, width: 100, cellStyle: { textAlign: 'center', fontWeight: '600' } },
        ],
      },
      // Hidden sort column — sorts grid by modifiedOn descending (most recent on top)
      { field: '_modifiedOn', hide: true, sort: 'desc', sortIndex: 0 },
    ];

    const requestToCol: ColDef = { headerName: 'Request To', field: 'requestTo', sortable: true, filter: 'agSetColumnFilter', width: 140 };
    const descriptionCol: ColDef = { headerName: 'Description', field: 'description', sortable: true, filter: false, width: 180, tooltipField: 'description' };

    this.columnDefs = this.effectiveIsManager
      ? [
          ...leaveColumns,
          descriptionCol,
          { headerName: 'Action', field: 'action', sortable: false, filter: false, width: 220, cellRenderer: managerActionRenderer },
        ]
      : this.canViewAllRequests()
      ? [...leaveColumns, requestToCol]
      : [
          ...leaveColumns,
          { headerName: 'Status', field: 'status', sortable: true, filter: 'agSetColumnFilter', width: 130, cellRenderer: statusBadgeRenderer },
        ];

    const hourlyColumns: (ColDef | ColGroupDef)[] = [
      { headerName: 'Date', field: 'date', sortable: true, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY, width: 110 },
      { headerName: 'Employee', field: 'employeeName', sortable: true, filter: 'agSetColumnFilter', flex: 1, minWidth: 180, cellRenderer: EmployeeCellRendererComponent, wrapText: true, autoHeight: true },
      { headerName: 'Emp ID', field: 'empId', sortable: true, filter: 'agSetColumnFilter', width: 100 },
      { headerName: 'Department', field: 'department', sortable: true, filter: 'agSetColumnFilter', width: 120 },
      { headerName: 'Permission Type', field: 'permissionType', sortable: true, filter: 'agSetColumnFilter', width: 140 },
      {
        headerName: 'Hours Requested',
        marryChildren: true,
        children: [
          { headerName: 'From', field: 'fromTime', sortable: true, filter: 'agSetColumnFilter', width: 110 },
          { headerName: 'To', field: 'toTime', sortable: true, filter: 'agSetColumnFilter', width: 110 },
          { headerName: 'Total Hours', field: 'totalHours', sortable: true, filter: false, width: 100, cellStyle: { textAlign: 'center', fontWeight: '600' } },
        ],
      },
      // Hidden sort column — sorts grid by modifiedOn descending (most recent on top)
      { field: '_modifiedOn', hide: true, sort: 'desc', sortIndex: 0 },
    ];

    this.hourlyColumnDefs = this.effectiveIsManager
      ? [
          ...hourlyColumns,
          { headerName: 'Action', field: 'action', sortable: false, filter: false, width: 220, cellRenderer: hourlyManagerActionRenderer },
        ]
      : this.canViewAllRequests()
      ? [...hourlyColumns]
      : [
          ...hourlyColumns,
          { headerName: 'Status', field: 'status', sortable: true, filter: 'agSetColumnFilter', width: 130, cellRenderer: statusBadgeRenderer },
        ];
  }

  private setupGridOptions(): void {
    this.customGridOptions = {
      suppressCellFocus: true,
      suppressMovableColumns: true,
      suppressRowHoverHighlight: false,
      enableCellTextSelection: false,
      groupHeaderHeight: 22,
      paginationAutoPageSize: true,
      // Columns are all fixed-width (no flex), so on wide screens they leave
      // a dead gap after the last column; stretch them to fill it instead.
      onGridReady: (params: any) => params.api.sizeColumnsToFit(),
      onGridSizeChanged: (params: any) => params.api.sizeColumnsToFit(),
    };
  }

  private getDateRange(): { fromDate: string; toDate: string } {
    const from = new Date(this.currentYear, this.currentMonth, 1);
    const to = new Date(this.currentYear, this.currentMonth + 1, 0);
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { fromDate: fmt(from), toDate: fmt(to) };
  }

  private getRequestSource(): Observable<any[]> {
    const { fromDate, toDate } = this.getDateRange();
    if (this.effectiveIsManager) {
      return this.leaveService.getTeamLeaveRequests({ fromDate, toDate });
    }

    return this.leaveService.getAllLeaveRequestsForHR({ fromDate, toDate });
  }

  private safeDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const d = parseLocalDate(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private fmtDate(value: string | Date | null | undefined): string {
    if (!value) return '-';
    return formatDateMDY(value) || '-';
  }

  private mapLeaveRow(row: any): LeaveRequestRow {
    const start = this.safeDate(row.startDate);
    const end = this.safeDate(row.endDate);
    const computedDays = start && end
      ? Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
      : 0;

    return {
      id: row.id ?? '',
      date: this.fmtDate(row.createdOn),
      employeeName: row.userName ?? '',
      employeeRole: row.designationName ?? '',
      employeeAvatar: '',
      empId: row.userCode ?? row.userId ?? '',
      department: row.departmentName ?? '—',
      leaveType: row.leaveTypeName ?? '',
      from: this.fmtDate(row.startDate),
      to: this.fmtDate(row.endDate),
      totalDays: computedDays > 0 ? Math.max(row.totalDays ?? 0, computedDays) : (row.totalDays ?? 0),
      requestTo: row.requestToName ?? '—',
      status: this.toManagerViewStatus(row.status),
      description: String(row.reason ?? '').trim() || '-',
      startDateRaw: start ? `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}` : '',
      endDateRaw: end ? `${end.getFullYear()}-${String(end.getMonth()+1).padStart(2,'0')}-${String(end.getDate()).padStart(2,'0')}` : '',      // ISO date for hidden sort column — most recently modified/approved on top
      _modifiedOn: (() => {
        const d = this.safeDate(row.approvedOn ?? row.rejectedOn ?? row.createdOn);
        return d ? d.toISOString() : '';
      })(),    };
  }

  private mapHourlyRow(row: any): HourlyPermissionRow {
    const start = this.safeDate(row.startDate);
    const end = this.safeDate(row.endDate);
    const hours = start && end ? Math.max(0, (end.getTime() - start.getTime()) / 3_600_000) : 0;
    const totalHours = hours ? String(Math.floor(hours)).padStart(2, '0') : '00';

    return {
      id: row.id ?? '',
      date: this.fmtDate(row.startDate),
      employeeName: row.userName ?? '',
      employeeRole: row.designationName ?? '',
      employeeAvatar: '',
      empId: row.userCode ?? row.userId ?? '',
      department: row.departmentName ?? '—',
      permissionType: row.leaveTypeName ?? '-',
      fromTime: start
        ? start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
        : '-',
      toTime: end
        ? end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
        : '-',
      totalHours,
      status: this.toManagerViewStatus(row.status),
      isHourly: true,
      startIsoRaw: start ? start.toISOString() : '',
      startDateRaw: start ? `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}` : '',
      endDateRaw: end ? `${end.getFullYear()}-${String(end.getMonth()+1).padStart(2,'0')}-${String(end.getDate()).padStart(2,'0')}` : '',      // ISO date for hidden sort column — most recently modified on top
      _modifiedOn: (() => {
        const d = this.safeDate(row.approvedOn ?? row.rejectedOn ?? row.createdOn);
        return d ? d.toISOString() : '';
      })(),    };
  }

  /** Returns true if the mapped row's start date is in the past (manager can't act on it) */
  private isRowPastDated(row: any): boolean {
    if (row.isHourly) {
      const startIso = row.startIsoRaw as string | undefined;
      const startDt = parseLocalDate(startIso);
      return !isNaN(startDt.getTime()) ? startDt <= new Date() : false;
    }
    const raw = row.startDateRaw;
    if (!raw) return false;
    const d = parseLocalDate(raw);
    if (isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }

  private loadLeaveAndHourlyData(): void {
    this.isLoading = true;
    this.isHourlyLoading = true;
    if (this.effectiveIsManager) {
      this.isManagerPanelLoading = true;
    }

    this.getRequestSource()
      .pipe(catchError(() => of([])), takeUntilDestroyed(this.destroyRef))
      .subscribe(requests => {
        const rows = Array.isArray(requests) ? requests : [];

        // Show all requests for managers (including past-dated ones)
        const leaveOnly = rows.filter((row: any) => row.isHourlyLeaveRequest !== true);
        this.rowData = leaveOnly
          .map(row => this.mapLeaveRow(row))
          .sort((a, b) => (a.status === 'Pending' ? 0 : 1) - (b.status === 'Pending' ? 0 : 1));
        this.totalRequests = this.rowData.length;
        if (this.effectiveIsManager) {
          this.pendingLeaveCount = this.rowData.filter(r =>
            r.status.trim().toLowerCase() === 'pending'
          ).length;
        }
        this.isLoading = false;

        const now = new Date();
        // Show all hourly requests for managers (including past ones)
        const hourlyOnly = rows
          .filter((row: any) => row.isHourlyLeaveRequest === true);
        this.hourlyRowData = hourlyOnly
          .map(row => this.mapHourlyRow(row))
          .sort((a, b) => (a.status === 'Pending' ? 0 : 1) - (b.status === 'Pending' ? 0 : 1));
        this.totalHourlyRequests = this.hourlyRowData.length;
        if (this.effectiveIsManager) {
          this.pendingHourlyCount = this.hourlyRowData.filter(r =>
            r.status.trim().toLowerCase() === 'pending'
          ).length;
        }
        this.isHourlyLoading = false;

        // Build manager panel data from the same rows — avoids a separate API call
        // that was filtering by requestToEmployeeId and missing leaves approved by
        // this manager but originally sent to someone else.
        if (this.effectiveIsManager) {
          const today = this.getTodayStart();
          const allFutureRows = rows.filter((row: any) => {
            const d = this.safeDate(row.startDate);
            if (!d) return false;
            d.setHours(0, 0, 0, 0);
            return d >= today;
          });
          this.managerPanelRowData = allFutureRows
            .filter((row: any) => row.isHourlyLeaveRequest !== true)
            .map(row => this.mapLeaveRow(row));
          this.managerHourlyPanelRowData = allFutureRows
            .filter((row: any) => row.isHourlyLeaveRequest === true)
            .map(row => {
              const start = this.safeDate(row.startDate);
              const end   = this.safeDate(row.endDate);
              const dateRaw = start
                ? `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}`
                : '';
              const fmtTime = (d: Date | null) =>
                d ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-';
              return {
                ...this.mapLeaveRow(row),
                startDateRaw: dateRaw,
                endDateRaw:   dateRaw,
                from: fmtTime(start),
                to:   fmtTime(end),
              } as LeaveRequestRow;
            });
          this.isManagerPanelLoading = false;
        }
      });
  }
}
